import React, { useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    TextField,
    Typography
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AppleIcon from '@mui/icons-material/Apple';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Cookies from 'js-cookie';

import logo from '../files/images/logo.png';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ForgotPassword from '../components/forgot-password';
import WorkInProgress from '../components/popup/work-in-progress';
import ChooseCompany from '../components/choose-company';
import CompanySetup from '../components/company-setup';

import useApi, { SIGNED_OUT_REASON_KEY } from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';
import { useUser } from '../providers/user-provider';
import { useToast } from '../providers/toaster-provider';
import { IDLE_LOGOUT_FLAG_KEY } from '../components/idle-timeout';
import { Close } from '@mui/icons-material';

const googleClientId = "863216338324-08ea9cnavjgc7ncliav29d6lanajcasg.apps.googleusercontent.com";
const microsoftClientId = "7cf3faa1-d92c-48aa-a2e0-1e8e304625b7";

// PKCE helper functions for Microsoft OAuth
const generateCodeVerifier = (): string => {
    const array = new Uint32Array(56);
    window.crypto.getRandomValues(array);
    return Array.from(array, dec => ('0' + dec.toString(16)).substr(-2)).join('');
};

const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    const bytes = new Uint8Array(digest);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
};

const getCenteredPopupSpecs = (width: number, height: number): string => {
    const left = Math.round((window.screen.width - width) / 2);
    const top = Math.round((window.screen.height - height) / 2);
    
    return [
        `width=${width}`,
        `height=${height}`,
        `left=${left}`,
        `top=${top}`,
        'scrollbars=yes',
        'resizable=yes',
        'toolbar=no',
        'menubar=no',
        'location=no',
        'status=no'
    ].join(',');
};

// Carousel slides data
const carouselSlides = [
    {
        img: 'https://img1.wsimg.com/isteam/getty/2174758709/:/rs=w:1920,m',
        caption: 'Secure and reliable',
    },
    {
        img: 'https://images.pexels.com/photos/1533483/pexels-photo-1533483.jpeg?cs=srgb&dl=light-dawn-landscape-1533483.jpg&fm=jpg',
        caption: 'Visualize your spending habits',
    },
    {
        img: 'https://img1.wsimg.com/isteam/getty/141467949/:/cr=t:0%25,l:5.66%25,w:88.68%25,h:100%25/rs=w:600,h:451,cg:true',
        caption: 'Track your finances with ease',
    },
];

export const Carousel: React.FC = () => {
    // Custom carousel indicator styles
    const carouselStyles = `
    .carousel-dots .slick-dots {
        bottom: 30px;
        display: flex !important;
        justify-content: center;
        align-items: center;
    }
    .carousel-dots .slick-dots li {
        width: fit-content;
        height: 10px;
        margin: 0 6px;
    }
    .carousel-dots .slick-dots li button::before {
        display: none;
    }
    .carousel-dots .slick-dots li button {
        width: 10px;
        height: 10px;
        padding: 0;
        border-radius: 50%;
        background: rgba(255, 255, 255, 1);
        border: none;
        transition: all 0.3s ease;
    }
    .carousel-dots .slick-dots li.slick-active button {
        background: #00B89F;
        width: 30px;
        height: 10px;
        border-radius: 6px;
    }
    .carousel-dots .slick-dots li button:hover {
        background: rgba(255, 255, 255, 0.8);
    }
    `;
    const settings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 5000,
        arrows: false,
        pauseOnHover: true,
        accessibility: false
    };
    return (
        <Box maxWidth={500} width="100%" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
            <style>{carouselStyles}</style>
            <Box width="100%" className="carousel-dots">
                <Slider {...settings}>
                {carouselSlides.map((slide, i) => (
                    <Box key={i} display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%">
                    <img
                        src={slide.img}
                        alt={slide.caption}
                        style={{ width: 500, height: 600, borderRadius: 12, marginBottom: 16 }}
                    />
                    </Box>
                ))}
                </Slider>
            </Box>
        </Box>
    );
};

const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const DISABLE_MICROSOFT_OAUTH = true;
const DISABLE_GOOGLE_OAUTH = true;
const DISABLE_APPLE_OAUTH = true;

const Login: React.FC = () => {
    const { refreshUser } = useUser();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const showChooseCompanyParam = searchParams.get('chooseCompany') === 'true';
    const createCompanyParam = searchParams.get('createCompany') === 'true';
    const createCompanyEmail = searchParams.get('email') || '';
    const [showPassword, setShowPassword] = useState(false);
    const [remember, setRemember] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [forgotStep, setForgotStep] = useState(1);
    const [error, setError] = useState<{ email?: string; password?: string, credentials?: string }>({});
    const [socialLoading, setSocialLoading] = useState<'apple' | 'google' | 'microsoft' | null>(null);
    const [showCompanySelection, setShowCompanySelection] = useState(showChooseCompanyParam);
    const [showCreateCompany, setShowCreateCompany] = useState(false);
    const [companies, setCompanies] = useState<any[]>([]);
    const [pendingUserData, setPendingUserData] = useState<any>(null);
    const processedLoginDataRef = React.useRef<any>(null);

    const { showError, showInfo } = useToast();

    const [login, setLogin] = useApi();

    // Landed here from the 15-minute inactivity sign-out (idle-timeout.tsx) -
    // say why, once, so it doesn't look like a crash or a lost session.
    React.useEffect(() => {
        try {
            const serverReason = localStorage.getItem(SIGNED_OUT_REASON_KEY);
            localStorage.removeItem(SIGNED_OUT_REASON_KEY);
            if (localStorage.getItem(IDLE_LOGOUT_FLAG_KEY)) {
                localStorage.removeItem(IDLE_LOGOUT_FLAG_KEY);
                showInfo('You were signed out after 15 minutes of inactivity. Please sign in again.');
            } else if (serverReason) {
                // Signed out by the server - e.g. an admin changed this user's access.
                showInfo(serverReason);
            }
        } catch { /* storage blocked - nothing to show */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initialize company selection from URL param
    React.useEffect(() => {
        if (showChooseCompanyParam) {
            const entitiesCookie = Cookies.get('entities');
            if (entitiesCookie) {
                try {
                    const entities = JSON.parse(entitiesCookie);
                    setCompanies(entities);
                    setShowCompanySelection(true);
                } catch {
                    setShowCompanySelection(false);
                }
            }
        }
    }, [showChooseCompanyParam]);

    // Apple Sign-In configuration and initialization
    React.useEffect(() => {
        if (typeof window !== 'undefined' && (window as any).AppleID) {
            (window as any).AppleID.auth.init({
                clientId: process.env.REACT_APP_APPLE_CLIENT_ID || 'com.finants.web', // Replace with your Apple service ID
                scope: 'name email',
                redirectURI: window.location.origin,
                state: 'state'
            });
        }
    }, []);

    React.useEffect(() => {
        document.title = "FineAnts | Login";
    }, []);
    
    // Apple Sign-In handler with popup
    const handleAppleSignIn = async () => {
        try {
            setSocialLoading('apple');
            if (DISABLE_APPLE_OAUTH) return;
            if (typeof window !== 'undefined' && (window as any).AppleID) {
                // Configure Apple Sign-In to use popup
                (window as any).AppleID.auth.init({
                    clientId: process.env.REACT_APP_APPLE_CLIENT_ID || 'com.finants.web',
                    scope: 'name email',
                    redirectURI: window.location.origin,
                    state: 'state',
                    usePopup: true
                });
                
                const response = await (window as any).AppleID.auth.signIn();
                
                console.log('Apple Sign-In Success:', response);
                
                // Handle successful Apple sign-in
                if (response && response.authorization && response.authorization.code) {
                    handleOAuthLogin('apple', response.authorization.code, response.user);
                } else {
                    showError('Social login failed. Please try again or use your email and password.');
                }
                
            } else {
                throw new Error('Apple Sign-In SDK not loaded');
            }
        } catch (error: any) {
            console.error('Apple Sign-In Error:', error);
            showError('Social login failed. Please try again or use your email and password.');
        } finally {
            setSocialLoading(null);
        }
    };

    // Google Sign-In handler with popup
    const handleGoogleSignIn = () => {
        setSocialLoading('google');
        if (DISABLE_GOOGLE_OAUTH) return;
        
        const clientId = googleClientId;
        const redirectUri = encodeURIComponent(window.location.origin + '/auth/google/callback');
        const scope = encodeURIComponent('openid email profile');
        const responseType = 'code';
        const state = Math.random().toString(36).substring(2, 15);
        
        const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
            `client_id=${clientId}&` +
            `redirect_uri=${redirectUri}&` +
            `response_type=${responseType}&` +
            `scope=${scope}&` +
            `state=${state}`;
        
        // Open popup window (centered)
        const popup = window.open(
            googleAuthUrl,
            'googleSignIn',
            getCenteredPopupSpecs(500, 600)
        );
        
        // Listen for popup messages
        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            
            if (event.data.type === 'GOOGLE_SIGN_IN_SUCCESS') {
                console.log('Google Sign-In Success:', event.data);
                handleOAuthLogin('google', event.data.code);
                popup?.close();
            } else if (event.data.type === 'GOOGLE_SIGN_IN_ERROR') {
                console.error('Google Sign-In Error:', event.data.error);
                showError('Social login failed. Please try again or use your email and password.');
                popup?.close();
            }
            
            setSocialLoading(null);
            window.removeEventListener('message', handleMessage);
        };
        
        window.addEventListener('message', handleMessage);
        
        // Handle popup blocked or closed - COOP-safe version
        const checkClosed = setInterval(() => {
            try {
                if (popup?.closed) {
                    clearInterval(checkClosed);
                    setSocialLoading(null);
                    window.removeEventListener('message', handleMessage);
                }
            } catch (error) {
                // COOP policy blocks window.closed access - stop checking
                clearInterval(checkClosed);
                console.log('COOP policy detected for Google popup, monitoring stopped');
            }
        }, 1000);
    };

    // Microsoft Sign-In handler with popup and PKCE
    const handleMicrosoftSignIn = async () => {
        setSocialLoading('microsoft');
        if (DISABLE_MICROSOFT_OAUTH) return;
        
        try {
            // Generate PKCE parameters
            const codeVerifier = generateCodeVerifier();
            const codeChallenge = await generateCodeChallenge(codeVerifier);
            
            // Store code verifier for later use
            sessionStorage.setItem('pkce_code_verifier', codeVerifier);
            
            // Microsoft OAuth popup configuration with PKCE
            const clientId = microsoftClientId;
            const redirectUri = window.location.origin + '/auth/microsoft/callback';
            const scope = 'openid profile email User.Read';
            const responseType = 'code';
            const state = Math.random().toString(36).substring(2, 15);
            
            const microsoftAuthUrl = `https://login.microsoftonline.com/consumers/oauth2/v2.0/authorize?` +
                `client_id=${encodeURIComponent(clientId)}&` +
                `redirect_uri=${encodeURIComponent(redirectUri)}&` +
                `response_type=${encodeURIComponent(responseType)}&` +
                `scope=${encodeURIComponent(scope)}&` +
                `state=${encodeURIComponent(state)}&` +
                `code_challenge=${encodeURIComponent(codeChallenge)}&` +
                `code_challenge_method=S256&` +
                `response_mode=fragment`;
            
            // Open popup window (centered)
            const popup = window.open(
                microsoftAuthUrl,
                'microsoftSignIn',
                getCenteredPopupSpecs(500, 600)
            );
        
            // Listen for popup messages
            const handleMessage = (event: MessageEvent) => {
                if (event.origin !== window.location.origin) return;
                
                if (event.data.type === 'MICROSOFT_SIGN_IN_SUCCESS') {
                    console.log('Microsoft Sign-In Success:', event.data);
                    handleOAuthLogin('microsoft', event.data.code);
                    popup?.close();
                } else if (event.data.type === 'MICROSOFT_SIGN_IN_ERROR') {
                    console.error('Microsoft Sign-In Error:', event.data);
                    showError('Social login failed. Please try again or use your email and password.');
                    popup?.close();
                }
                
                setSocialLoading(null);
                window.removeEventListener('message', handleMessage);
            };
            
            window.addEventListener('message', handleMessage);
            
            // Handle popup blocked or closed - Microsoft COOP-safe version
            const checkClosed = setInterval(() => {
                try {
                    if (popup?.closed) {
                        clearInterval(checkClosed);
                        setSocialLoading(null);
                        window.removeEventListener('message', handleMessage);
                    }
                } catch (error) {
                    // COOP policy blocks window.closed access - stop checking
                    clearInterval(checkClosed);
                    console.log('COOP policy detected, popup monitoring stopped');
                }
            }, 1000);
            
            // Fallback timeout for Microsoft OAuth (5 minutes)
            setTimeout(() => {
                clearInterval(checkClosed);
                setSocialLoading(null);
                window.removeEventListener('message', handleMessage);
                console.log('Microsoft OAuth popup timeout');
            }, 300000);
            
        } catch (error) {
            console.error('Microsoft OAuth PKCE error:', error);
            showError('Social login failed. Please try again or use your email and password.');
            setSocialLoading(null);
        }
    };

    React.useEffect(()=> {
        const { data, isLoading, error } = login;
        
        // Prevent re-processing the same login data
        if (data && processedLoginDataRef.current === data) {
            return;
        }
        
        if (data && !isLoading) {
            processedLoginDataRef.current = data;
            
            Cookies.set('entities', JSON.stringify(data.entities || []), { expires: remember ? 30 : undefined });
            Cookies.set('token', data.token, { expires: remember ? 30 : undefined });
            
            // Check if createCompany param is set and the logged-in email matches the one that triggered the flow
            if (createCompanyParam && (!createCompanyEmail || email.trim().toLowerCase() === createCompanyEmail.trim().toLowerCase())) {
                // Go directly to create company step
                setPendingUserData(data.user);
                setCompanies(data.entities || []);
                setShowCreateCompany(true);
                localStorage.setItem('inCompanySelection', 'true');
                setLogin(null);
            }
            // Check if user has multiple companies
            else if (data.user?.entityCount > 1) {
                // Store user data in state for company selection
                setPendingUserData(data.user);
                setCompanies(data.entities || []);
                setShowCompanySelection(true);
                // Flag that we're in company selection mode
                localStorage.setItem('inCompanySelection', 'true');
                // Reset login state to prevent re-running this effect
                setLogin(null);
            } else {
                // Single company - save user and refresh
                Cookies.set('user', JSON.stringify(data.user), { expires: remember ? 30 : undefined });
                // Reset login state to prevent re-running this effect
                setLogin(null);
                refreshUser();
            }
        } else if (error) {
            setError({ credentials: 'Email or password is incorrect' });
            setLogin(null);
        }
    }, [login, remember, refreshUser, createCompanyParam, createCompanyEmail, email, setLogin]);

    const validate = () => {
        const newErrors: { email?: string; password?: string } = {};
        if (!email || !isValidEmail(email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!password) {
            newErrors.password = 'Please enter your password';
        }
        setError(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const handleOAuthLogin = async (provider: string, code: string, userData?: any) => {
        try {
            setSocialLoading(provider as 'google' | 'microsoft' | 'apple');
            
            const { url, method } = buildApiRequestConfig(routes.user.oauth);
            
            const requestBody: any = {
                provider: provider,
                code: code,
                redirectUri: window.location.origin + `/auth/${provider}/callback`,
                mode: 'login'
            };
            
            // Add user data for Apple (if provided)
            if (provider === 'apple' && userData) {
                requestBody.user = userData;
            }
            
            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });
            
            const data = await response.json();
            
            // Check if social account already exists
            if (data.error === "Social account already exists") {
                showError(data.details || 'This social account is already registered. Please use login mode.');
                setSocialLoading(null);
                return;
            }
            
            if (response.ok && data.success) {
                // Store token and entities
                Cookies.set('entities', JSON.stringify(data.entities || []), { expires: remember ? 30 : undefined });
                Cookies.set('token', data.token, { expires: remember ? 30 : undefined });
                
                // Check if createCompany param is set and the social login email matches
                if (createCompanyParam && (!createCompanyEmail || (data.user?.email || '').trim().toLowerCase() === createCompanyEmail.trim().toLowerCase())) {
                    // Go directly to create company step
                    setPendingUserData(data.user);
                    setCompanies(data.entities || []);
                    setShowCreateCompany(true);
                    localStorage.setItem('inCompanySelection', 'true');
                }
                // Check if user has multiple companies
                else if (data.user?.entityCount > 1) {
                    // Store user data in state for company selection
                    setPendingUserData(data.user);
                    setCompanies(data.entities || []);
                    setShowCompanySelection(true);
                    // Flag that we're in company selection mode
                    localStorage.setItem('inCompanySelection', 'true');
                } else {
                    // Single company - save user and refresh
                    Cookies.set('user', JSON.stringify(data.user), { expires: remember ? 30 : undefined });
                    refreshUser();
                }
            } else {
                showError(data.details || data.message || 'Social login failed. Please try again or use your email and password.');
            }
        } catch (error) {
            console.error(`${provider} OAuth Error:`, error);
            showError('Social login failed. Please try again or use your email and password.');
        } finally {
            setSocialLoading(null);
        }
    };
    
    const handleLogin = () => {
        if (!validate()) return;
        const { url, method } = buildApiRequestConfig(routes.user.login);
        setLogin({ url, method, body: { email: email.trim(), password } });
    };

    return (
        <Box width={'100vw'} minHeight={'100vh'} bgcolor="var(--color-page-background)">
        <WorkInProgress 
            open={socialLoading === 'apple' || socialLoading === 'microsoft' || socialLoading === 'google'} 
            onClose={() => setSocialLoading(null)}/>
        <Box 
            display="flex" 
            minHeight="100vh" 
            bgcolor="var(--color-page-background)" 
            maxWidth={1200} 
            margin="0 auto"
            sx={{
                flexDirection: { xs: 'column', md: 'row' },
                '@media (max-width: 600px)': {
                    minHeight: 'auto',
                    paddingTop: '60px'
                }
            }}
        >
        <Box
            sx={{
                position: 'absolute', 
                top: 50,
                left: 0,
                boxSizing: 'border-box',
                '@media (max-width: 600px)': { top: 8, left: 8 },
            }}
            width="100%" 
        >
            <Box maxWidth="1200px" m="0 auto" px={{ xs: '16px', sm: '64px' }} boxSizing="border-box">
                <img src={logo} alt="FineAnts" style={{ height: 45 }} />
            </Box>
        </Box>
        <Box 
            flex={1} 
            display="flex" 
            flexDirection="column" 
            justifyContent="center" 
            px={{ xs: 3, sm: 8 }}
            py={{ xs: 3, md: 0 }}
        >
            <Box width="100%" maxWidth={400}>
                {showCreateCompany ? (
                    <CompanySetup
                        onSuccess={(company) => {
                            // Auto-select the newly created company
                            if (pendingUserData) {
                                const userData = { ...pendingUserData };
                                userData.entityId = company.entityId;
                                Cookies.set('user', JSON.stringify(userData), { expires: remember ? 30 : undefined });
                                setPendingUserData(null);
                                setLogin(null);
                            }
                            Cookies.set('selectedCompany', JSON.stringify(company));
                            setShowCreateCompany(false);
                            localStorage.removeItem('inCompanySelection');
                            refreshUser();
                        }}
                        onBack={() => {
                            setShowCreateCompany(false);
                            Cookies.remove('token');
                            Cookies.remove('entities');
                            setPendingUserData(null);
                            setEmail('');
                            setPassword('');
                            processedLoginDataRef.current = null;
                            localStorage.removeItem('inCompanySelection');
                        }}
                    />
                ) : showCompanySelection ? (
                    <ChooseCompany
                        companies={companies}
                        onSelectCompany={(company) => {
                            // Update user with selected company's entityId
                            if (pendingUserData) {
                                const userData = { ...pendingUserData };
                                userData.entityId = company.entityId;
                                Cookies.set('user', JSON.stringify(userData), { expires: remember ? 30 : undefined });
                                setPendingUserData(null);
                                // Reset login state to prevent re-running effects
                                setLogin(null);
                            }
                            Cookies.set('selectedCompany', JSON.stringify(company));
                            setShowCompanySelection(false);
                            localStorage.removeItem('inCompanySelection');
                            refreshUser();
                        }}
                        onCompanyCreated={(newCompany) => {
                            // Add new company to the list
                            setCompanies([...companies, newCompany]);
                        }}
                        onBack={() => {
                            setShowCompanySelection(false);
                            Cookies.remove('token');
                            Cookies.remove('entities');
                            Cookies.remove('selectedCompany');
                            setPendingUserData(null);
                            setEmail('');
                            setPassword('');
                            processedLoginDataRef.current = null;
                            localStorage.removeItem('inCompanySelection');
                        }}
                    />
                ) : (location.pathname === '/forgot-password') ? (
                    <ForgotPassword
                        email={email}
                        setEmail={setEmail}
                        onBack={() => { navigate('/login'); setForgotStep(1); }}
                        step={forgotStep}
                        setStep={setForgotStep}
                        socialLogin={<SocialLoginButtons 
                            onAppleClick={handleAppleSignIn}
                            onGoogleClick={handleGoogleSignIn}
                            onMicrosoftClick={handleMicrosoftSignIn}
                            loading={socialLoading}
                        />}
                    />
                    ) : (
                    <>
                        <Typography variant="h4" fontWeight={600} mb={1}>
                            Login
                        </Typography>
                        {error.credentials && 
                            <Box sx={{ border: '1px solid red', borderRadius: 1, py: 0.75, backgroundColor: 'rgba(255, 0, 0, 0.1)', position: 'relative' }}>
                                <Typography color="red" fontSize={14} align="center">
                                    {error.credentials}
                                </Typography>
                                <IconButton
                                    size="small"
                                    sx={{ position: 'absolute', top: 1, right: 2 }}
                                    onClick={() => setError({ ...error, credentials: '' })}
                                >
                                    <Close fontSize="small" />
                                </IconButton>
                            </Box>
                        }
                        <TextField
                            label="Email"
                            fullWidth
                            margin="normal"
                            value={email}
                            error={!!error.email || !!error.credentials}
                            helperText={error.email}
                            onChange={e => setEmail(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleLogin();
                                }
                            }}
                            InputProps={{ style: { background: 'var(--color-page-background)' } }}
                        />
                        <TextField
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            margin="normal"
                            value={password}
                            error={!!error.password || !!error.credentials}
                            helperText={error.password}
                            onChange={e => setPassword(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleLogin();
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(s => !s)} edge="end">
                                            {showPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                style: { background: 'var(--color-page-background)' }
                            }}
                            sx={{
                                '& input::-ms-reveal, & input::-ms-clear': { display: 'none' },
                                '& input::-webkit-contacts-auto-fill-button, & input::-webkit-credentials-auto-fill-button': { display: 'none' },
                            }}
                        />
                        <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} mb={2}>
                            <FormControlLabel
                            control={<Checkbox checked={remember} onChange={e => setRemember(e.target.checked)} />}
                            label={<Typography fontSize={14}>Remember me</Typography>}
                            />
                            <Typography color="#FF8682" fontSize={14} sx={{ cursor: 'pointer' }} onClick={() => navigate('/forgot-password')}>
                                Forgot Password
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            fullWidth
                            sx={{ bgcolor: 'var(--color-primary)', color: '#fff', fontWeight: 600, py: 1.2, borderRadius: 1, boxShadow: 'none', mb: 1, mt: 1 }}
                            onClick={handleLogin}
                        >
                            Login
                        </Button>
                        <Typography align="center" fontSize={14} color="text.secondary" mb={2}>
                            Don't have an account? <span style={{ color: '#FF8682', cursor: 'pointer' }} onClick={() => navigate('/signup')}>Sign up</span>
                        </Typography>
                        <Divider sx={{ my: 2, color: '#B3B3B3', fontSize: 14 }}>Or login with</Divider>
                        <SocialLoginButtons 
                            onAppleClick={() => setSocialLoading('apple')}
                            onGoogleClick={handleGoogleSignIn}
                            onMicrosoftClick={() => setSocialLoading('microsoft')}
                            loading={socialLoading}
                        />
                    </>
                    )}
                </Box>
            </Box>
            {/* Right Side - Carousel (Hidden on Mobile) */}
            <Box 
                flex={1} 
                display="flex" 
                alignItems="center" 
                justifyContent="center" 
                padding={{ xs: 0, md: '0 64px' }}
                sx={{
                    display: { xs: 'none', md: 'flex' }
                }}
            >
                <Carousel />
            </Box>
        </Box>
        </Box>
    );
};

export const SocialLoginButtons: React.FC<{
    onAppleClick?: () => void;
    onGoogleClick?: () => void;
    onMicrosoftClick?: () => void;
    loading?: 'apple' | 'google' | 'microsoft' | null;
}> = ({ onAppleClick, onGoogleClick, onMicrosoftClick, loading }) => {
    const buttonStyle = {
        borderRadius: 1,
        borderColor: 'var(--color-primary)',
        flex: 1,
        height: 40
    };

    return (
        <Box display="flex" justifyContent="center" gap={1}>
            <Button 
                variant="outlined" 
                sx={buttonStyle }
                onClick={onMicrosoftClick}
                title='Microsoft'
                disabled={loading === 'microsoft'}
            >
                <MicrosoftIcon size={20}/>
            </Button>
            <Button 
                variant="outlined" 
                sx={buttonStyle }
                onClick={onGoogleClick}
                disabled={loading === 'google'}
                title='Google'
            >
                <GoogleIcon size={20} />
            </Button>
            <Button 
                variant="outlined" 
                sx={buttonStyle }
                onClick={onAppleClick}
                disabled={loading === 'apple'}
                title='Apple'
            >
                <AppleIcon sx={{ color: '#000' }} />
            </Button>
        </Box>
    );
};

const MicrosoftIcon: React.FC<{ size?: number; color?: string }> = ({ size = 24, color = '#000' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
        <rect x="13" y="1" width="10" height="10" fill="#7FBA00"/>
        <rect x="1" y="13" width="10" height="10" fill="#00A4EF"/>
        <rect x="13" y="13" width="10" height="10" fill="#FFB900"/>
    </svg>
);

const GoogleIcon: React.FC<{ size?: number; color?: string }> = ({ size = 24, color = '#000' }) => (
    <svg width={size} height={size} viewBox="0 0 533.5 544.3" xmlns="http://www.w3.org/2000/svg">
        <path fill="#4285F4" d="M533.5 278.4c0-17.4-1.4-34.1-4-50.4H272v95.5h147.1c-6.4 34.7-25.9 64.1-55.3 83.8v69.5h89.5c52.2-48 82.2-119 82.2-198.4z"/>
        <path fill="#34A853" d="M272 544.3c74.7 0 137.4-24.7 183.2-67l-89.5-69.5c-24.9 16.7-56.7 26.6-93.7 26.6-71.9 0-132.8-48.6-154.7-114.1H27.4v71.9c45.5 89.4 139.1 152.1 244.6 152.1z"/>
        <path fill="#FBBC05" d="M117.3 324.3c-10.6-31.8-10.6-66.1 0-97.9V154.5H27.4c-39.5 77.9-39.5 170.7 0 248.6l89.9-78.8z"/>
        <path fill="#EA4335" d="M272 107.7c39.6-.6 77.4 14.2 106.1 41.2l79.4-79.4C407.1 24.3 341.4-1.3 272 0 166.5 0 72.9 62.7 27.4 152.1l89.9 71.9c22-65.5 82.9-114.1 154.7-116.3z"/>
    </svg>
);

export default Login;
